import { type Dispatch, type SetStateAction, useEffect, useState } from 'react'

/**
 * useState that survives a component unmount/remount for the life of the SPA —
 * so switching between section tabs (Schedule ↔ Rules) keeps each page's filters
 * and choices instead of resetting them. Backed by a module-level store, so a
 * full browser reload starts fresh (which is where a page's own defaults apply).
 */
const store = new Map<string, unknown>()

export function usePersistentState<T>(
  key: string,
  initial: T,
): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() =>
    store.has(key) ? (store.get(key) as T) : initial,
  )
  useEffect(() => {
    store.set(key, value)
  }, [key, value])
  return [value, setValue]
}

/**
 * Set what a page's persistent state opens on, before the page is reached.
 *
 * A link that means "this sheet" — the home page's "see this Shabbos's times" —
 * hands the schedule its answer this way rather than through the address, so
 * the schedule keeps one way of holding what it shows.
 */
export function seedPersistentState<T>(key: string, value: T): void {
  store.set(key, value)
}
